import React, { useEffect, useState } from 'react';
import { observer, inject } from 'mobx-react';
import { IMixedMediaInjectedProps } from '../../types';
import styles from './mixedMedia.scss';
import classNames from 'classnames';
import Zoom from '../icons/zoom';
import { useViewportWidth } from '../../helpers/viewPortWidth';
import { Carousel } from 'react-responsive-carousel';
import IconArrowCircleGreyLeft from '../icons/icon_arrow_circle_grey_left';
import IconArrowCircleGreyRight from '../icons/icon_arrow_circle_grey_right';
import { IMixedMediaOfBrick } from '../../stores/types';
import { decodeHTML } from '../../helpers/html-helper';
import { loadDetailsModal, loadMediaOverlay } from '../lazyComponents';

const MixedMedia = inject('store')(
    observer(({ store, floatElementRef, stickyElementRef }: IMixedMediaInjectedProps) => {

        const extraSmallBreakpointWidth = 375;
        const widthForServerSideRendering = store?.isMobileShop ? extraSmallBreakpointWidth : 1024;
        const { width } = typeof window !== 'undefined' ? useViewportWidth() : { width: widthForServerSideRendering };
        const [isClient, setIsClient] = useState(false);
        let column4 = 0;
        useEffect(() => {
            setIsClient(true);
        }, []);

        useEffect(() => {
            if (width > 768) {
                if (floatElementRef.current && stickyElementRef.current) {
                    store?.setLinkScroll(stickyElementRef.current, floatElementRef.current);
                }
            }
        }, [isClient, width]);

        function getLayoutClass() {
            const classes: string[] = [];
            if (store?.dataStore.mixedMedia.sortedMixedMediaForBricks.length) {
                const elementCount = store?.mixedMediaStore.getImageCount;
                if (store?.dataStore.mixedMedia.flexibleSetImageFullPath) {
                    classes.push('fsp-has_set_image');
                }
                if (elementCount > 7) {
                    classes.push('fsp-grid_max');
                }
                else {
                    classes.push(`fsp-grid_${elementCount}`);
                }
                if (elementCount & 1) {
                    classes.push('fsp-grid_odd');
                }
            }

            return classes;
        };

        function getSlideLayoutClass(itemsCount: number, index: number) {
            const classes: string[] = [];
            classes.push(`fsp-slide_${itemsCount}`);
            if (index == 0 && store?.dataStore.mixedMedia.flexibleSetImageFullPath) {
                classes.push('fsp-has_set_image');
            }
            return classes;
        };

        function calculateLayoutColumns() {
            if (store?.mixedMediaStore.getImageCount) {
                const elementCount = store?.mixedMediaStore.getImageCount;
                if (elementCount % 4 == 0) {
                    column4 = elementCount / 4;
                }
                else if (elementCount % 3 != 0) {
                    column4 = Math.floor(elementCount / 4);
                    let r = elementCount % 4;
                    while (r % 3 != 0) {
                        column4--;
                        r += 4;
                    }
                }
            }
        }

        function getItemClassForRow(index) {
            const classes: string[] = [];
            if (column4 * 4 > index) {
                classes.push('fsp-column_4');
            }
            else {
                classes.push('fsp-column_3');
            }
            return classes.join(' ');
        }

        const handleZoomClick = (mainImgIndex: number) => {
            void loadMediaOverlay();
            store?.mixedMediaStore.updateMediaOverlayVisibility(true, mainImgIndex);
        };

        const handleDetailsClick = (item: IMixedMediaOfBrick) => {
            void loadDetailsModal();
            store?.productInfoStore.setItemOrigin('FlexSets-0-picture');
            store?.mixedMediaStore.setDetailsModalOpen(true, item.masterArticleNo, item.brickNo);
        };

        const renderArrowPrev = (onClickHandler, hasPrev) =>
            hasPrev && (
                <div data-testid='carousel-arrow-prev'
                    data-semantic-id='carousel-arrow-prev'
                    className={'control_arrow a_previous'}
                    onClick={onClickHandler}>
                    <IconArrowCircleGreyLeft />
                </div>
            );

        const renderArrowNext = (onClickHandler, hasPrev) =>
            hasPrev && (
                <div data-testid='carousel-arrow-next'
                    data-semantic-id='carousel-arrow-next'
                    className={'control_arrow a_next'}
                    onClick={onClickHandler}>
                    <IconArrowCircleGreyRight />
                </div>
            );

        const hasOnHoverImage = (item: IMixedMediaOfBrick) => {
            return item.mainActionImageFullPath ||
                (item.additionalInfoImagesFullPaths && item.additionalInfoImagesFullPaths.length > 0) ||
                item.shoeSoleImageFullPath || item.secondaryImageFullPath;
        }

        const getOnHoverImage = (item: IMixedMediaOfBrick) => {
            if (item.mainActionImageFullPath) {
                return item.mainActionImageFullPath;
            }
            if (item.additionalInfoImagesFullPaths && item.additionalInfoImagesFullPaths.length > 0) {
                return item.additionalInfoImagesFullPaths[0];
            }
            if (item.shoeSoleImageFullPath) {
                return item.shoeSoleImageFullPath;
            }
            if (item.secondaryImageFullPath) {
                return item.secondaryImageFullPath;
            }
        }

        function returnMixedMedia() {
            calculateLayoutColumns();
            return (
                <>
                    <div className={styles.holder} ref={floatElementRef}></div>
                    <div className={classNames(styles.mixedMedia, getLayoutClass())} ref={stickyElementRef}>
                    {
                        store?.mixedMediaStore.getSetImage &&
                        <div className={classNames(styles.mixedMediaItem,
                            { [getItemClassForRow(0)]: store?.mixedMediaStore.getImageCount > 7 }
                        )}>
                            <div className={styles.mixed_media_slide}>
                                <img className={styles.image} src={store?.mixedMediaStore.getSetImage}
                                    alt={store.mixedMediaStore.altTagForFlexibleSetImage}>
                                </img>
                            </div>

                        </div>
                    }
                    {
                        store?.mixedMediaStore.getMixedMediaImages.map((item, index) => {
                            const sortedBrickSelections = store?.dataStore.orderModule.sortedBrickSelections;
                            const brickSelection = sortedBrickSelections.find(sortedBrickSelection =>
                                sortedBrickSelection.brickNo === item.brickNo);

                            return (
                                <div key={index} className={classNames(styles.mixedMediaItem,
                                    {
                                        [getItemClassForRow(index + (store?.mixedMediaStore.getSetImage ? 1 : 0))]:
                                            store?.mixedMediaStore.getImageCount > 7
                                    }
                                )}>
                                    <div className={styles.mixed_media_slide}>
                                        <img className={styles.image}
                                            alt={store.mixedMediaStore.getAltValue(index)}
                                            src={item.imageFullPath}>
                                        </img>
                                        <div className={styles.eye_catcher_container}>
                                            {
                                                item.isFreeInSet && <div className={styles.free_in_set}>
                                                    {decodeHTML(store.localization.generalL10n.gratis)}
                                                </div>
                                            }
                                            {
                                                item.includedQuantity > 1 && <div className={styles.quantity_icon}>
                                                    {item.includedQuantity}x</div>
                                            }
                                        </div>
                                    </div>

                                    {hasOnHoverImage(item) &&
                                        <div className={classNames(styles.mixed_media_slide,
                                            styles.additional_info_image)}>
                                            <img className={styles.image} src={getOnHoverImage(item)}
                                                alt={store.mixedMediaStore.getAltValue(index)}></img>
                                            <div className={styles.zoom_icon}
                                                onClick={() => handleZoomClick(index + 1)}
                                                onMouseEnter={loadMediaOverlay}
                                                onFocus={loadMediaOverlay}
                                                onTouchStart={loadMediaOverlay}>
                                                <Zoom />
                                            </div>
                                        </div>
                                    }

                                    <div className={styles.details_button_container}>
                                        <div className={styles.details_button}
                                            data-testid='flexset-picture-layer_0'
                                            data-semantic-id='flexset-picture-layer_0'
                                            data-flexset-id={store.dataStore.orderModule.flexSetInfo.fsCode}
                                            data-flexset-name={store.dataStore.orderModule.flexSetInfo.displayName}
                                            data-flexset-module-layer_0-index={index}
                                            data-flexset-module-layer_0-category={brickSelection?.mainNavigationKeyPath}
                                            data-flexset-item-name={brickSelection?.salesDesignation}
                                            data-flexset-item-sku={store.getSalesArticleNo(
                                                brickSelection?.salesArticleVariantKey.toString())}
                                            onClick={() => handleDetailsClick(item)}
                                            onMouseEnter={loadDetailsModal}
                                            onFocus={loadDetailsModal}
                                            onTouchStart={loadDetailsModal}
                                        >
                                            {store?.localization.generalL10n.details}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                    </div>
                </>
            )
        }

        const shouldShowStatus = () => {
            if (store) {
                return store.mixedMediaStore.getImagesForCarousel(width <= extraSmallBreakpointWidth).length > 1;
            }
            else {
                return false;
            }
        }

        function returnMobileMixedMedia() {
            return (
                <div className={classNames(styles.mixedMedia, getLayoutClass())}>
                    <Carousel
                        showArrows={true}
                        showIndicators={false}
                        showStatus={shouldShowStatus()}
                        showThumbs={false}
                        className='mobile-mixedMedia-carousel'
                        infiniteLoop={shouldShowStatus()}
                        renderArrowPrev={renderArrowPrev}
                        renderArrowNext={renderArrowNext}
                        statusFormatter={(currentItem, total) => {
                            return `${currentItem} / ${total}`;
                        }}
                    >
                        {

                            store?.mixedMediaStore.getImagesForCarousel(width <= extraSmallBreakpointWidth)
                                .map((item, index) => {
                                    return (
                                        <div key={index} className={classNames(styles.mobile_mixedMedia_slide,
                                            getSlideLayoutClass(item.images.length, index),
                                        )}>
                                            {
                                                item.images.map((image, index) => {
                                                    return (
                                                        <div key={index} className={styles.mixedMediaItem}>
                                                            <img className={styles.image} src={image.fullPath}
                                                                alt={image.alt} />
                                                            <div className={styles.eye_catcher_container}>
                                                                {
                                                                    image.isFreeInSet &&
                                                                    <div className={styles.free_in_set}>
                                                                        {decodeHTML(store.localization.generalL10n.gratis)}
                                                                    </div>
                                                                }
                                                                {
                                                                    image.includedQuantity > 1 &&
                                                                    <div className={styles.quantity_icon}>
                                                                        {image.includedQuantity}x
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>

                                                    )
                                                })
                                            }
                                        </div>
                                    )
                                })
                        }
                    </Carousel>
                </div>

            )
        }
        return (
            <div className={classNames(styles.mixedMediaContainer, store?.isEsApp ? styles.mixedMediaContainer_esApp : '')}>
                {
                    isClient ? width > 768 ? returnMixedMedia() : returnMobileMixedMedia() : <></>

                }
            </div>

        );
    }));

export default MixedMedia;

